import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import productShippingMetadataService from '../services/productShippingMetadataService';

export const useProductShippingMetadataList = (searchTerm?: string) => {
  return useQuery({
    queryKey: ['productShippingMetadata', 'list', searchTerm],
    queryFn: () => productShippingMetadataService.getAll(searchTerm),
  });
};

export const useProductShippingMetadataByRetailer = (retailer: string, searchTerm?: string, enabled = true) => {
  return useQuery({
    queryKey: ['productShippingMetadata', 'byRetailer', retailer, searchTerm],
    queryFn: () => productShippingMetadataService.getByRetailer(retailer, searchTerm),
    enabled: enabled && !!retailer,
  });
};

export const useProductShippingMetadataDetail = (
  productId: string,
  retailer: string,
  enabled = false
) => {
  return useQuery({
    queryKey: ['productShippingMetadata', 'detail', productId, retailer],
    queryFn: () => productShippingMetadataService.getByProductIdAndRetailer(productId, retailer),
    enabled: enabled && !!productId && !!retailer,
  });
};

export const useProductShippingMetadataMutations = () => {
  const queryClient = useQueryClient();

  const createMetadata = useMutation({
    mutationFn: (data: any) => productShippingMetadataService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productShippingMetadata'] });
      console.log('Shipping metadata created successfully');
    },
    onError: (error: any) => {
      console.error('Failed to create shipping metadata:', error?.response?.data?.message || error.message);
    },
  });

  const updateMetadata = useMutation({
    mutationFn: ({
      productId,
      retailer,
      data,
    }: {
      productId: string;
      retailer: string;
      data: any;
    }) => productShippingMetadataService.update(productId, retailer, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productShippingMetadata'] });
      console.log('Shipping metadata updated successfully');
    },
    onError: (error: any) => {
      console.error('Failed to update shipping metadata:', error?.response?.data?.message || error.message);
    },
  });

  const deleteMetadata = useMutation({
    mutationFn: ({ productId, retailer }: { productId: string; retailer: string }) =>
      productShippingMetadataService.delete(productId, retailer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productShippingMetadata'] });
      console.log('Shipping metadata deleted successfully');
    },
    onError: (error: any) => {
      console.error('Failed to delete shipping metadata:', error?.response?.data?.message || error.message);
    },
  });

  return { createMetadata, updateMetadata, deleteMetadata };
};

